"use client";

import type { ComponentProps } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/lib/analytics";

import { ArticleTag } from "@/components/layout/ArticleTag";
import { LockedIcon, UnlockedIcon } from "@/components/icons/MemberLockIcon";
import { cn } from "@/lib/utils";

type ArticleCardProps = {
  title: string;
  image?: { src: string; alt: string };
  tags?: string[];
  href: string;
  memberOnly?: boolean;
  isMember?: boolean;
  onPreviewClick?: () => void;
} & ComponentProps<"article">;

type ArticleTitleProps = {
  title: string;
  memberOnly?: boolean;
  isMember?: boolean;
  as?: "h3" | "p";
  className?: string;
};

/** Headline mit gelbem Schloss davor, wenn der Artikel Member-Only ist. */
const ArticleTitle = ({ title, memberOnly, isMember, as: Tag = "h3", className }: ArticleTitleProps) => (
  <div className={cn("flex items-start [&>*+*]:ml-[5px]", className)}>
    {memberOnly && (
      <span className="size-[25px] rounded-full bg-sun-100 flex items-center justify-center shrink-0">
        {isMember ? (
          <UnlockedIcon className="size-4 text-black" />
        ) : (
          <LockedIcon className="size-4 text-black" />
        )}
      </span>
    )}
    <Tag className="text-copy-bold text-black">{title}</Tag>
  </div>
);

const ArticleCard = ({
  title,
  image,
  tags,
  href,
  memberOnly,
  isMember,
  onPreviewClick,
  className,
  ...rest
}: ArticleCardProps) => {
  const pathname = usePathname();
  const isHub = pathname.startsWith("/reviews/");

  const handleClick = () => {
    if (isHub) {
      trackEvent("hub_article_clicked", {
        article_title: title,
        article_url: href,
        hub: pathname,
      });
    }
  };

  // Wie bei den Event-Karten steht die Farbe fuer den Zugang: sand fuer
  // kostenlos, sun fuer Member-Only. Das Schloss sitzt neben der Headline
  // statt auf dem Bild.
  const cardContent = (
    <>
      {/* Image */}
      <div className="h-[120px] relative w-full">
        {image?.src && (
          <Image
            src={image.src}
            alt={image.alt}
            fill
            sizes="(min-width: 1024px) 320px, 278px"
            className="object-cover"
          />
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <ArticleTitle title={title} memberOnly={memberOnly} isMember={isMember} />


        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="mt-auto pt-2">
            {/* Negative Raender statt flex-gap (iOS 14) */}
            <div className="flex flex-wrap -ml-1 -mt-1">
              {tags.map((tag) => (
                <ArticleTag key={tag} label={tag} linked={false} memberOnly={memberOnly} className="ml-1 mt-1" />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <article
      className={cn("bg-white rounded-2xl w-[278px] lg:w-[320px] overflow-hidden card-rotate flex flex-col", className)}
      {...rest}
    >
      {onPreviewClick ? (
        <div className="flex flex-col flex-1 cursor-pointer" onClick={onPreviewClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onPreviewClick()}>
          {cardContent}
        </div>
      ) : (
        <Link href={href} className="flex flex-col flex-1 cursor-pointer" onClick={handleClick}>
          {cardContent}
        </Link>
      )}
    </article>
  );
};

export { ArticleCard, ArticleTitle };
export type { ArticleCardProps, ArticleTitleProps };
